import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import Api from 'Api'
import Device from '../../models/Device'
import Patient from '../../models/Patient'
import User from '../../models/User'
import {call, getContext, put, select, takeEvery} from 'redux-saga/effects'
import {RootState} from 'redux/rootReducer'

export type SearchResult = {
  users: User[]
  patients: Patient[]
  devices: Device[]
}

type SearchState = {
  results: SearchResult
  loading: boolean
  isSearching: boolean
  error?: string
  query?: string
}

const initialState: SearchState = {
  results: {
    users: [],
    patients: [],
    devices: [],
  },
  loading: false,
  isSearching: false,
}

export const {actions, reducer} = createSlice({
  name: 'search',
  initialState,
  reducers: {
    search: (state: SearchState, action: PayloadAction<{query: string}>) => {
      const {query} = action.payload
      state.query = query
    },
    refreshSearch: (_state: SearchState, _action: PayloadAction<void>) => {},
    setLoading: (state: SearchState, action: PayloadAction<{loading: boolean}>) => {
      const {loading} = action.payload
      state.loading = loading
      state.isSearching = loading
    },
    setIsSearching: (state: SearchState, action: PayloadAction<{isSearching: boolean}>) => {
      const {isSearching} = action.payload
      state.isSearching = isSearching
      state.loading = isSearching
    },
    setError: (state: SearchState, action: PayloadAction<{error?: string}>) => {
      const {error} = action.payload
      state.error = error
    },
    setResults: (state: SearchState, action: PayloadAction<{results: SearchResult}>) => {
      const {results} = action.payload
      state.results = results
    },
  },
})

let latestSearchRequestId = 0

const isAbortError = (error: unknown) => {
  return (error as Error)?.name === 'AbortError'
}

const searchSaga = function*(action: ReturnType<typeof actions.search>) {
  const requestId = ++latestSearchRequestId
  const {query} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setIsSearching({isSearching: true}))
  yield put(actions.setResults({results: {users: [], patients: [], devices: []}}))
  try {
    const trimmed = (query || '').trim()
    const results: SearchResult = yield call(api.search, trimmed)

    const linkedPatients = results.users
      .filter(u => u.patient)
      .map(u => u.patient!)
    const mergedPatients = [
      ...results.patients,
      ...linkedPatients.filter(lp => !results.patients.some(p => p.id === lp.id)),
    ]
    let finalResults: SearchResult = {...results, patients: mergedPatients}

    yield put(actions.setResults({results: finalResults}))
  } catch (error) {
    if (isAbortError(error)) {
      return
    }
    throw error
  } finally {
    if (requestId === latestSearchRequestId) {
      yield put(actions.setIsSearching({isSearching: false}))
    }
  }
}


const refreshSearchSaga = function*() {
  const {query} = yield select(({search}: RootState) => search)
  if (!query || !query.trim()) return
  yield put(actions.search({query}))
}

export const saga = function*() {
  yield takeEvery(actions.search, searchSaga)
  yield takeEvery(actions.refreshSearch, refreshSearchSaga)
}
